/**
 * Per-row schedule revisions and the forward-only status lattice.
 *
 * Old rows have no `rev` (treated as 0). A client that sends `baseRev`
 * must name the revision it edited. A stale baseRev does not overwrite.
 * A client that omits `baseRev` still merges, and a status cannot move
 * backwards unless the write is an explicit human reopen.
 * Tombstone ids are never dropped. Timed records are additive metadata.
 */

export const TOMBSTONE_TTL_MS = 90 * 24 * 60 * 60 * 1000;

export type TombstoneKind = "inbox" | "mission" | "flightLog";

export type TimedTombstone = {
  id: string;
  kind: TombstoneKind;
  deletedAt: string;
  purgeAfter: string;
};

export type RowMergeConflict = {
  id: string;
  baseRev: number;
  currentRev: number;
  current: Record<string, unknown>;
};

const MISSION_STATUS_RANK: Record<string, number> = {
  pending: 0,
  "in-progress": 1,
  completed: 2,
};

const LIFECYCLE_RANK: Record<string, number> = {
  scheduled: 0,
  accepted: 1,
  active: 2,
  taxi: 3,
  airborne: 4,
  diverting: 4,
  arrived: 5,
  diverted: 5,
  completed: 6,
};

const STICKY_STATUS_KEYS = [
  "status",
  "lifecycle",
  "completedAt",
  "acceptedAt",
  "startedAt",
  "arrivedAtUtc",
  "hoursApplied",
  "ledgerPosted",
  "statusAuthority",
  "statusAuthorityAt",
  "activeBoundAt",
  "cancelledAt",
  "cancelReason",
  "cancelReasonNote",
  "preCancelStatus",
  "actualDepartureUtc",
  "actualDepartureSource",
] as const;

export function readRowRev(row: unknown): number {
  if (!row || typeof row !== "object") return 0;
  const rev = Number((row as { rev?: unknown }).rev);
  if (!Number.isFinite(rev) || rev <= 0) return 0;
  return Math.floor(rev);
}

export function readBaseRev(row: unknown): number | null {
  if (!row || typeof row !== "object") return null;
  if (!Object.prototype.hasOwnProperty.call(row, "baseRev")) return null;
  const rev = Number((row as { baseRev?: unknown }).baseRev);
  if (!Number.isFinite(rev) || rev < 0) return null;
  return Math.floor(rev);
}

export function isHumanReopen(row: unknown): boolean {
  if (!row || typeof row !== "object") return false;
  const rec = row as { reopen?: unknown; statusEvent?: unknown; statusAuthority?: unknown; reopenReason?: unknown };
  const flagged = rec.reopen === true || rec.statusEvent === "reopen";
  if (!flagged) return false;
  if (rec.statusAuthority !== "desk" && rec.statusAuthority !== "megan") return false;
  return typeof rec.reopenReason === "string" && rec.reopenReason.trim().length > 0;
}

function looksLikeMission(row: unknown): boolean {
  if (!row || typeof row !== "object") return false;
  const rec = row as { status?: unknown; lifecycle?: unknown; originIcao?: unknown; callsign?: unknown; flightNumber?: unknown };
  const life = typeof rec.lifecycle === "string" ? rec.lifecycle : "";
  if (life && Object.prototype.hasOwnProperty.call(LIFECYCLE_RANK, life)) return true;
  if (life === "cancelled") return true;
  const status = typeof rec.status === "string" ? rec.status : "";
  if (status !== "pending" && status !== "in-progress" && status !== "completed" && status !== "cancelled") return false;
  return Boolean(rec.originIcao || rec.callsign || rec.flightNumber);
}

/** True when `next` would move a trip backwards. Cancel is a terminal write, not a regression. */
export function isStatusRegression(prev: unknown, next: unknown): boolean {
  if (!looksLikeMission(prev) || !looksLikeMission(next)) return false;
  if (isHumanReopen(next)) return false;
  const prevRec = prev as { status?: string; lifecycle?: string };
  const nextRec = next as { status?: string; lifecycle?: string };
  const prevStatus = MISSION_STATUS_RANK[prevRec.status ?? ""];
  const nextStatus = MISSION_STATUS_RANK[nextRec.status ?? ""];
  if (prevStatus != null && nextStatus != null && nextStatus < prevStatus) return true;
  if (
    (prevRec.status === "completed" || prevRec.status === "cancelled") &&
    (nextRec.status === "pending" || nextRec.status === "in-progress")
  ) {
    return true;
  }
  const prevLife = LIFECYCLE_RANK[prevRec.lifecycle ?? ""];
  const nextLife = LIFECYCLE_RANK[nextRec.lifecycle ?? ""];
  if (prevLife != null && nextLife != null && nextLife < prevLife) return true;
  if (
    (prevRec.lifecycle === "completed" || prevRec.lifecycle === "cancelled") &&
    nextLife != null &&
    nextLife < LIFECYCLE_RANK.completed
  ) {
    return true;
  }
  return false;
}

function stripWire(row: Record<string, unknown>): Record<string, unknown> {
  const copy = { ...row };
  delete copy.baseRev;
  delete copy.reopen;
  delete copy.statusEvent;
  if (!(Number(copy.rev) > 0)) delete copy.rev;
  return copy;
}

function restoreStickyStatus(prev: Record<string, unknown>, row: Record<string, unknown>): void {
  for (const key of STICKY_STATUS_KEYS) {
    if (prev[key] !== undefined) row[key] = prev[key];
    else delete row[key];
  }
}

/**
 * Merge one schedule row.
 * `baseRev` must match the stored rev or the stored row is kept.
 * Missing `baseRev` keeps today's field merge, except a status cannot regress.
 */
export function mergeRevisionRow(
  prev: Record<string, unknown> | null | undefined,
  incoming: Record<string, unknown>,
  nowIso?: string,
): { row: Record<string, unknown>; conflict: RowMergeConflict | null } {
  const baseRev = readBaseRev(incoming);
  const prevRev = prev ? readRowRev(prev) : 0;
  const nextRev = readRowRev(incoming);
  const id = String(incoming.id ?? prev?.id ?? "").trim();
  if (prev && baseRev !== null && baseRev !== prevRev) {
    return {
      row: prev,
      conflict: { id, baseRev, currentRev: prevRev, current: prev },
    };
  }
  if (prev && baseRev === null && nextRev > 0 && prevRev > 0 && nextRev < prevRev) {
    return { row: prev, conflict: null };
  }
  const cleaned = stripWire(incoming);
  let row: Record<string, unknown>;
  if (!prev) {
    row = { ...cleaned };
    if (baseRev !== null) row.rev = Math.max(prevRev, baseRev) + 1;
    if (nowIso && baseRev !== null) row.updatedAt = nowIso;
  } else if (baseRev !== null && baseRev === prevRev) {
    row = { ...prev, ...cleaned, rev: prevRev + 1 };
    if (nowIso) row.updatedAt = nowIso;
  } else {
    row = { ...prev, ...cleaned };
    if (prevRev > 0) row.rev = prevRev;
    else delete row.rev;
  }
  if (prev && !isHumanReopen(incoming) && isStatusRegression(prev, row)) restoreStickyStatus(prev, row);
  return { row, conflict: null };
}

export function hydrateTombstoneRecords(raw: unknown): TimedTombstone[] {
  if (!Array.isArray(raw)) return [];
  const out: TimedTombstone[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const rec = row as Partial<TimedTombstone>;
    const id = typeof rec.id === "string" ? rec.id.trim() : "";
    const kind = rec.kind === "inbox" || rec.kind === "mission" || rec.kind === "flightLog" ? rec.kind : null;
    const deletedAt = typeof rec.deletedAt === "string" ? rec.deletedAt : "";
    const purgeAfter = typeof rec.purgeAfter === "string" ? rec.purgeAfter : "";
    if (!id || !kind || !deletedAt || !purgeAfter) continue;
    const key = `${kind}:${id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ id, kind, deletedAt, purgeAfter });
  }
  return out;
}

export function tombstoneRecord(id: string, kind: TombstoneKind, atIso: string): TimedTombstone | null {
  const trimmed = id.trim();
  if (!trimmed) return null;
  const deletedMs = Date.parse(atIso);
  const deletedAt = Number.isFinite(deletedMs) ? new Date(deletedMs).toISOString() : new Date().toISOString();
  const purgeAfter = new Date(Date.parse(deletedAt) + TOMBSTONE_TTL_MS).toISOString();
  return { id: trimmed, kind, deletedAt, purgeAfter };
}

/** Union timed records. The earlier delete wins so a later push cannot shorten the block. */
export function mergeTimedTombstones(local: unknown, remote: unknown): TimedTombstone[] {
  const byKey = new Map<string, TimedTombstone>();
  for (const row of [...hydrateTombstoneRecords(local), ...hydrateTombstoneRecords(remote)]) {
    const key = `${row.kind}:${row.id}`;
    const prev = byKey.get(key);
    if (!prev || row.deletedAt < prev.deletedAt) byKey.set(key, row);
  }
  return [...byKey.values()];
}

export function ensureTombstoneRecords(
  records: readonly TimedTombstone[],
  groups: Array<{ kind: TombstoneKind; ids: readonly string[] }>,
  atIso: string,
): TimedTombstone[] {
  const next = [...records];
  const seen = new Set(next.map((row) => `${row.kind}:${row.id}`));
  for (const group of groups) {
    for (const id of group.ids) {
      const key = `${group.kind}:${String(id).trim()}`;
      if (!String(id).trim() || seen.has(key)) continue;
      const stamped = tombstoneRecord(String(id), group.kind, atIso);
      if (!stamped) continue;
      seen.add(key);
      next.push(stamped);
    }
  }
  return next;
}

/**
 * Drop expired timed records. The id lists stay, so a purged record cannot
 * resurrect a trip, a log, or an inbox row.
 */
export function gcTimedTombstones(
  records: readonly TimedTombstone[],
  nowMs: number,
): { kept: TimedTombstone[]; purged: TimedTombstone[] } {
  const kept: TimedTombstone[] = [];
  const purged: TimedTombstone[] = [];
  for (const row of records) {
    const purgeMs = Date.parse(row.purgeAfter);
    if (Number.isFinite(purgeMs) && purgeMs <= nowMs) purged.push(row);
    else kept.push(row);
  }
  return { kept, purged };
}

export function tombstoneIds(records: readonly TimedTombstone[], kind?: TombstoneKind): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const row of records) {
    if (kind && row.kind !== kind) continue;
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    out.push(row.id);
  }
  return out;
}
